import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '../components/SEO';
import PublicNav from '../components/PublicNav';

/**
 * Features Page Component
 *
 * Public-facing features page for Nivoon with detailed descriptions
 * of key platform capabilities. Matches the visual style of the Landing page.
 */
export function Features(): React.ReactElement {
  const navigate = useNavigate();

  const features = [
    {
      title: 'AI Intelligence Hub',
      subtitle: 'Your AI-powered requirements command center',
      description: 'Consolidate all AI-driven analysis into a single dashboard. The AI Intelligence Hub evaluates your entire requirements set and delivers actionable insights across four key dimensions.',
      capabilities: [
        {
          name: 'Requirements Scorecard',
          detail: 'Score every requirement on completeness, clarity, testability, and consistency. Get an overall project quality rating and per-requirement recommendations for improvement.',
        },
        {
          name: 'Risk Identification',
          detail: 'Automatically surface 15-25+ project risks based on your requirements. Each risk includes probability, impact scoring, mitigation strategies, and linked requirements.',
        },
        {
          name: 'Test Case Generation',
          detail: 'Generate functional, edge-case, and negative test cases directly from your requirements. Export to CSV or JSON for integration with your test management tools.',
        },
        {
          name: 'Gap Analysis',
          detail: 'Identify missing requirements based on your project type. Get a coverage score and prioritized recommendations for areas that need additional specification.',
        },
      ],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      color: 'indigo',
    },
    {
      title: 'AI-Powered Requirements Generation',
      subtitle: 'Go from project idea to structured requirements in seconds',
      description: 'The New Project Wizard uses GPT-4o to generate 25-30 contextual requirements based on your project name, description, and type. Each requirement comes with a title, description, priority, category, and acceptance criteria.',
      capabilities: [
        {
          name: 'Context-Aware Generation',
          detail: 'AI analyzes your project type, description, and objectives to generate requirements that are specific and relevant to your domain, not generic boilerplate.',
        },
        {
          name: 'Acceptance Criteria Included',
          detail: 'Every generated requirement includes 3-5 testable acceptance criteria so your team can immediately understand what "done" looks like.',
        },
        {
          name: 'Review Before Saving',
          detail: 'Preview all generated requirements before committing. Edit titles, descriptions, and priorities inline, or exclude individual requirements you don\'t need.',
        },
        {
          name: 'Template Fallback',
          detail: 'If AI generation is unavailable, the system automatically falls back to curated requirement templates for your project type, ensuring you always get a starting point.',
        },
      ],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      ),
      color: 'violet',
    },
    {
      title: 'Requirements Management',
      subtitle: 'Organize, track, and version your requirements at scale',
      description: 'A complete requirements lifecycle management system with hierarchical organization, version history, threaded discussions, and AI-powered enhancement insights for every requirement.',
      capabilities: [
        {
          name: 'Hierarchical Requirements',
          detail: 'Organize requirements into parent-child relationships with collapsible nested display. Break down high-level epics into detailed child requirements while maintaining traceability.',
        },
        {
          name: 'Workflow Dependencies',
          detail: 'Define finish-to-start, start-to-start, and other dependency types between requirements. Circular dependency detection prevents invalid configurations.',
        },
        {
          name: 'AI Enhancement Insights',
          detail: 'Get AI-powered suggestions to improve individual requirements. The lightbulb icon shows which requirements have insights, with best-practice recommendations for clarity and completeness.',
        },
        {
          name: 'Threaded Discussions',
          detail: 'Comment and reply on any requirement with threaded conversations. Keep stakeholder feedback organized and tied to the specific requirement it relates to.',
        },
      ],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      ),
      color: 'blue',
    },
    {
      title: 'JIRA Cloud Integration',
      subtitle: 'Bi-directional sync between Nivoon and JIRA',
      description: 'Push requirements to JIRA as issues and receive real-time status updates back. Map Nivoon projects to JIRA projects, configure default issue types and priorities, and keep both systems in sync automatically.',
      capabilities: [
        {
          name: 'One-Click Push to JIRA',
          detail: 'Push individual requirements or bulk-push all unsynced requirements to JIRA with a single click. Issues are created with your configured defaults for issue type and priority.',
        },
        {
          name: 'Real-Time Status Sync',
          detail: 'JIRA status changes sync back to Nivoon automatically via webhooks. See JIRA status badges directly in your requirements table without switching tools.',
        },
        {
          name: 'Encrypted Credentials',
          detail: 'JIRA API tokens are encrypted at rest using AES-256-GCM. Your credentials are never stored in plain text and are only decrypted when making API calls.',
        },
        {
          name: 'Sync Dashboard',
          detail: 'Track sync statistics for every project: total requirements, synced, pending, and errors at a glance. Identify and resolve sync issues quickly.',
        },
      ],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      ),
      color: 'blue',
    },
    {
      title: 'Interactive Gantt Charts',
      subtitle: 'Visualize and manage your project timeline',
      description: 'A drag-to-edit Gantt chart for every project. Adjust requirement schedules by dragging timeline bars, track dependencies visually, and commit batched changes with a single save operation.',
      capabilities: [
        {
          name: 'Drag-to-Edit Scheduling',
          detail: 'Click and drag requirement bars to adjust start and end dates visually. A pending changes banner shows all modifications before you commit them to the database.',
        },
        {
          name: 'Multiple View Modes',
          detail: 'Switch between day, week, month, and year views to see your timeline at the right level of detail. Filter by status or priority to focus on what matters.',
        },
        {
          name: 'Dependency Visualization',
          detail: 'Requirement dependencies render as arrows between Gantt bars, making it easy to see which tasks block others and identify the critical path.',
        },
        {
          name: 'Priority Color Coding',
          detail: 'Gantt bars are color-coded by priority: red for critical, orange for high, blue for medium, and green for low. Unscheduled requirements are visually flagged for attention.',
        },
      ],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      color: 'green',
    },
    {
      title: 'Executive Summary & BRD Generation',
      subtitle: 'AI-generated documents with professional PDF export',
      description: 'Generate polished executive summaries and business requirements documents from your project data with a single click. AI analyzes your requirements, team, tech stack, and personas to produce comprehensive, stakeholder-ready content.',
      capabilities: [
        {
          name: 'One-Click Generation',
          detail: 'AI synthesizes your project name, description, requirements, technology stack, personas, and team composition into a cohesive executive summary or BRD.',
        },
        {
          name: 'Professional PDF Export',
          detail: 'Export generated documents as professionally formatted PDFs with custom typography, structured sections, and consistent branding, ready to share with stakeholders.',
        },
        {
          name: 'Markdown Rendering',
          detail: 'Generated content renders as rich markdown in the application with proper headings, bullet points, and emphasis, so you can review and share without exporting.',
        },
        {
          name: 'Editable Output',
          detail: 'Edit the generated summary or BRD directly in the application before exporting. Add context, refine language, or incorporate feedback from your team.',
        },
      ],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      color: 'cyan',
    },
    {
      title: 'Project Metrics & Analytics',
      subtitle: 'Data-driven visibility into project health',
      description: 'A dedicated analytics dashboard for every project with five key metric cards that give project managers immediate visibility into progress, risks, team activity, and workload distribution.',
      capabilities: [
        {
          name: 'Progress Tracking',
          detail: 'See requirement completion rates broken down by status: draft, in review, approved, and implemented. Track velocity over time to predict delivery timelines.',
        },
        {
          name: 'Schedule Health',
          detail: 'Identify at-risk requirements approaching or past their deadlines. Surface scheduling conflicts before they become delivery blockers.',
        },
        {
          name: 'Discussion Hotspots',
          detail: 'Find the top 5 most-discussed requirements by comment and reply activity. High discussion volume often indicates complexity or disagreement that needs attention.',
        },
        {
          name: 'Team Workload',
          detail: 'Visualize workload distribution across team members with color-coded indicators. Identify team members who are overloaded or underutilized.',
        },
      ],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      color: 'orange',
    },
    {
      title: 'AI Chatbot Assistant',
      subtitle: 'A context-aware AI assistant for your requirements',
      description: 'An integrated chatbot that understands your project context, including requirements, personas, and tech stack. Ask questions, get recommendations, and even create or update requirements directly through conversation.',
      capabilities: [
        {
          name: 'Full Project Context',
          detail: 'The chatbot is aware of all your project\'s requirements, personas, technology stack, and conversation history, so responses are always relevant to your specific project.',
        },
        {
          name: 'Action Execution',
          detail: 'Ask the chatbot to create new requirements or update existing ones. It detects intent from natural language and presents changes for your approval before saving.',
        },
        {
          name: 'Token Usage Tracking',
          detail: 'Monitor AI usage per session, per user, and per company. Built-in tracking ensures visibility into API consumption and costs.',
        },
        {
          name: 'Persistent Sessions',
          detail: 'Chat sessions are saved per project so you can return to previous conversations. The chatbot remembers prior context within each session.',
        },
      ],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      ),
      color: 'purple',
    },
    {
      title: 'Project Whiteboard',
      subtitle: 'An infinite canvas for every project',
      description: 'A built-in collaborative whiteboard powered by tldraw for brainstorming, diagramming, and visual planning. Each project gets its own persistent canvas with auto-save.',
      capabilities: [
        {
          name: 'Infinite Canvas',
          detail: 'Draw, sketch, and diagram without size limits. The canvas scales to fit whatever your team needs to visualize, from quick wireframes to detailed architecture diagrams.',
        },
        {
          name: 'Rich Drawing Tools',
          detail: 'Access shapes, arrows, text, sticky notes, and freehand drawing tools. Everything you need for visual collaboration without leaving the platform.',
        },
        {
          name: 'Auto-Save',
          detail: 'Canvas state is automatically saved as you work. Return to your whiteboard at any time and pick up exactly where you left off.',
        },
        {
          name: 'Per-Project Isolation',
          detail: 'Each project has its own whiteboard with isolated data. Team members see the same canvas, keeping visual artifacts tied to the project they belong to.',
        },
      ],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      color: 'pink',
    },
    {
      title: 'Enterprise Security & Administration',
      subtitle: 'Multi-tenant isolation with granular access control',
      description: 'Built for B2B SaaS from the ground up with company-scoped data isolation, four-tier role-based access control, encrypted credential storage, and comprehensive audit logging.',
      capabilities: [
        {
          name: 'Multi-Tenant Data Isolation',
          detail: 'Every query is scoped to the authenticated user\'s company. Data from one organization is never visible to another, enforced at both the middleware and database query level.',
        },
        {
          name: 'Four-Tier RBAC',
          detail: 'Platform Admin, Company Admin, Project Manager, and Contributor roles provide granular permission control. Each role sees only the features and data appropriate to their access level.',
        },
        {
          name: 'Audit Logging',
          detail: 'All significant actions are logged to an activity trail, including who did what, when, and on which project. Platform admins get a cross-company activity feed for system-wide visibility.',
        },
        {
          name: 'Encrypted Credentials',
          detail: 'Third-party integration tokens (JIRA, etc.) are encrypted at rest using AES-256-GCM with unique initialization vectors. Credentials are only decrypted at the moment of use.',
        },
      ],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      color: 'slate',
    },
  ];

  const colorMap: Record<string, { bg: string; text: string; light: string; border: string }> = {
    indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600', light: 'bg-indigo-50', border: 'border-indigo-200' },
    violet: { bg: 'bg-violet-100', text: 'text-violet-600', light: 'bg-violet-50', border: 'border-violet-200' },
    blue: { bg: 'bg-blue-100', text: 'text-blue-600', light: 'bg-blue-50', border: 'border-blue-200' },
    green: { bg: 'bg-green-100', text: 'text-green-600', light: 'bg-green-50', border: 'border-green-200' },
    orange: { bg: 'bg-orange-100', text: 'text-orange-600', light: 'bg-orange-50', border: 'border-orange-200' },
    cyan: { bg: 'bg-cyan-100', text: 'text-cyan-600', light: 'bg-cyan-50', border: 'border-cyan-200' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-600', light: 'bg-purple-50', border: 'border-purple-200' },
    pink: { bg: 'bg-pink-100', text: 'text-pink-600', light: 'bg-pink-50', border: 'border-pink-200' },
    slate: { bg: 'bg-slate-100', text: 'text-slate-600', light: 'bg-slate-50', border: 'border-slate-200' },
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Features"
        description="AI requirements generation, JIRA integration, Gantt charts, whiteboards, analytics, and enterprise security. See everything Nivoon offers."
        path="/features"
      />

      <PublicNav activePage="features" />

      {/* Hero Header */}
      <section className="pt-20 pb-16 bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-white mb-6">
            Features built for modern<br />requirements teams
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
            From AI-powered generation to JIRA integration, Nivoon gives your team everything it needs to define, manage, and deliver requirements with confidence.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {features.map((f) => (
              <a
                key={f.title}
                href={`#${f.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className="px-4 py-2 bg-white/10 text-white text-sm rounded-full hover:bg-white/20 transition-colors border border-white/20"
              >
                {f.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Sections */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {features.map((feature, index) => {
              const colors = colorMap[feature.color] || colorMap.indigo;
              const isEven = index % 2 === 0;

              return (
                <div
                  key={feature.title}
                  id={feature.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}
                  className="scroll-mt-24"
                >
                  {/* Feature Header */}
                  <div className={`flex flex-col lg:flex-row gap-12 items-start ${!isEven ? 'lg:flex-row-reverse' : ''}`}>
                    {/* Text Content */}
                    <div className="lg:w-1/2">
                      <div className={`inline-flex items-center gap-3 mb-4`}>
                        <div className={`w-12 h-12 ${colors.bg} rounded-lg flex items-center justify-center ${colors.text}`}>
                          {feature.icon}
                        </div>
                        <span className={`text-sm font-semibold ${colors.text} uppercase tracking-wider`}>
                          {feature.title}
                        </span>
                      </div>
                      <h2 className="text-3xl font-bold text-gray-900 mb-4">
                        {feature.subtitle}
                      </h2>
                      <p className="text-lg text-gray-600 mb-8">
                        {feature.description}
                      </p>
                      <button
                        onClick={() => navigate('/register')}
                        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-blue-700 transition-all shadow-md hover:shadow-lg"
                      >
                        Get Started
                        <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </button>
                    </div>

                    {/* Capability Cards */}
                    <div className="lg:w-1/2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {feature.capabilities.map((cap) => (
                        <div
                          key={cap.name}
                          className={`p-5 rounded-xl border ${colors.border} ${colors.light} hover:shadow-md transition-shadow`}
                        >
                          <h4 className="font-semibold text-gray-900 mb-2">{cap.name}</h4>
                          <p className="text-sm text-gray-600 leading-relaxed">{cap.detail}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Divider (except last) */}
                  {index < features.length - 1 && (
                    <div className="mt-24 border-t border-gray-100"></div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-indigo-600 to-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to transform your requirements process?
          </h2>
          <p className="text-xl text-indigo-100 mb-8">
            Start free and upgrade as your team grows. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/register')}
              className="px-8 py-4 bg-white text-indigo-600 font-semibold rounded-lg hover:bg-gray-100 transition-all text-lg shadow-lg"
            >
              Start Your Free Trial
            </button>
            <button
              onClick={() => navigate('/pricing')}
              className="px-8 py-4 bg-indigo-700 text-white font-semibold rounded-lg border-2 border-white hover:bg-indigo-800 transition-all text-lg"
            >
              View Pricing
            </button>
          </div>
        </div>
      </section>

      {/* Footer - matches Landing page */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-2">
                <li><span className="text-white">Features</span></li>
                <li><a href="/pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Security</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Roadmap</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                <li><a href="/investor-relations" className="hover:text-white transition-colors">Investor Relations</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Resources</h3>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-white transition-colors">Documentation</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Community</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li><a href="/legal#privacy" className="hover:text-white transition-colors">Privacy</a></li>
                <li><a href="/legal#terms" className="hover:text-white transition-colors">Terms</a></li>
                <li><a href="/legal#cookie-policy" className="hover:text-white transition-colors">Cookie Policy</a></li>
                <li><a href="/legal#licenses" className="hover:text-white transition-colors">Licenses</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0 cursor-pointer" onClick={() => navigate('/')}>
              <img src="/logo1.png" alt="Nivoon" className="h-8 opacity-70" />
            </div>
            <p className="text-sm text-gray-400">
              &copy; 2025 Nivoon. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Features;
